"use client";

import { useMemo } from "react";
import styled from "styled-components";
import { Text } from "@lmn/design-system";
import media from "@/styles/mediaQueries";
import {
  getColorByPercentage,
  getLabelByPercentage,
} from "@/lib/Helpers/ratings";
import RatingBar from "@/components/UI/RatingBar/RatingBar";
import TextButton from "@/components/UI/TextButton/TextButton";
import { scrollToReviewsTab } from "../../Helpers/scrollToReviewsTab";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { EnvConfig } from "@/lib/Helpers/getServerConfig";

const FloatingReviewsWrapper = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
  min-width: 200px;
  max-width: 220px;
  overflow: hidden;
  padding: ${({ theme }) => theme.kallisto.space.space200};
  background-color: ${({ theme }) =>
    theme.kallisto.color.background.static.backgroundLayer01};
  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumCenterShadow};
  opacity: 0.9;
  z-index: 3;
  display: none;
  transition: opacity 150ms;
  &:hover {
    opacity: 1;
  }

  ${media.screenLgMin`
    display: block;
  `};
`;

const RatingSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space100};
  margin-bottom: ${({ theme }) => theme.kallisto.space.space200};
  margin-right: ${({ theme }) => theme.kallisto.space.space200};
`;

const RatingBadge = styled.div<{ $percentage: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  height: 42px;
  padding: 0 ${({ theme }) => theme.kallisto.space.space100};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
  background-color: ${({ $percentage, theme }) =>
    getColorByPercentage($percentage, theme)};
  color: ${({ $percentage, theme }) =>
    $percentage > 70
      ? theme.kallisto.color.text.static.textOnDark
      : theme.kallisto.color.text.static.textOnLight};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
  font-size: ${({ theme }) => theme.kallisto.font.size[200]};
`;

const RatingInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: ${({ theme }) => theme.kallisto.space.space200};
`;

const CategoryText = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space50};
`;

const RatingBarWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.kallisto.space.space50};
`;

interface FloatingReviewsProps {
  ratingAverage: number;
  reviewsCount: number;
  recommendationRate: number;
  ratingAverageCategories?: Record<string, number>;
  envConfig: EnvConfig;
  searchId: string;
}

export default function FloatingReviews({
  ratingAverage,
  reviewsCount,
  recommendationRate,
  ratingAverageCategories,
  envConfig,
  searchId,
}: FloatingReviewsProps) {
  // Sort categories by value descending and take top 5
  const sortedCategories = useMemo(() => {
    if (!ratingAverageCategories) return [];
    return Object.entries(ratingAverageCategories)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [ratingAverageCategories]);

  // Don't render if data is missing or no reviews
  if (!reviewsCount || ratingAverage === undefined || ratingAverage === null) {
    return null;
  }

  return (
    <FloatingReviewsWrapper>
      <RatingSection>
        <RatingBadge $percentage={recommendationRate}>
          {ratingAverage.toFixed(1)}
        </RatingBadge>
        <RatingInfo>
          <Text variant="Title5">
            {getLabelByPercentage(recommendationRate)}
          </Text>
          <Text variant="BodySmall" color="textSecondary">
            {reviewsCount.toLocaleString("de-DE")} Bewertungen
          </Text>
        </RatingInfo>
      </RatingSection>

      {sortedCategories.map(({ label, value }) => {
        const percentage = (value / 6) * 100;
        return (
          <CategoryContainer key={label}>
            <CategoryText>
              <Text variant="BodySmall">{label}:</Text>
              <Text variant="BodySmall" weight="bold">
                {value.toFixed(1)}
              </Text>
            </CategoryText>
            <RatingBarWrapper>
              <RatingBar percentage={percentage} />
            </RatingBarWrapper>
          </CategoryContainer>
        );
      })}

      <TextButton
        onClick={() => {
          scrollToReviewsTab();
          sendKibanaLog({ envConfig, message: "Reviews Link Clicked from Floating Gallery Reviews", category: "interaction", searchId });
        }}
        variant="action"
      >
        Mehr anzeigen
      </TextButton>
    </FloatingReviewsWrapper>
  );
}
